import { AutomaticParkingReportComponent } from './domain/reports/report/automatic-parking/automatic-parking-report/automatic-parking-report.component';
import { AdminGuard } from './base/guards/admin.guard';
import { ZoneDamageComponent } from './domain/zone-damage/zone-damage.component';
import { EditProfileComponent } from './base/edit-profile/edit-profile.component';
import { ParkingRangerSessionsComponent } from './domain/parking-ranger-sessions/parking-ranger-sessions.component';
import { ParkerRegistrationComponent } from './domain/parker-registration/parker-registration.component';
import { ParkerAdministrationComponent } from './domain/administration/parker-administration/parker-administration.component';
import { ForgotPasswordComponent } from './base/forgot-password/forgot-password.component';
import { ClientService } from './base/services/client/client.service';
import { ClientAdministrationComponent } from './domain/administration/client-administration/client-administration.component';
import { inject } from '@angular/core';
import { Routes } from '@angular/router';

import { UserManagementComponent } from './base/user-management/user-management.component';
import { LoginComponent } from './base/login/login.component';
import { ChangePasswordComponent } from './base/change-password/change-password.component';

import { HomeComponent } from './domain/home/home.component';
import { AuthGuard } from './base/guards/auth.guard';
import * as pages from './core/routing/page-names';
import { AdminOrClientAdminGuard } from './base/guards/admin-client-admin.guard';
import { SetPasswordComponent } from './base/set-password/set-password.component';
import { CompanyAdministrationComponent } from './domain/administration/company-administration/company-administration.component';
import { ReportsComponent } from './domain/reports/reports.component';
import { DashboardService } from './domain/home/dashboard/dashboard.service';
import { TagsReportComponent } from './domain/reports/report/tags/tags-report/tags-report.component';
import { ParkerRegistrationReportComponent } from './domain/reports/report/parker-registration/parker-registration-report/parker-registration-report.component';
import { FalseRecordingReportComponent } from './domain/reports/report/false-recording/false-recording-report/false-recording-report.component';
import { ParkerTurnoverReportComponent } from './domain/reports/report/parker-turnover/parker-turnover-report/parker-turnover-report.component';
import { ParkerExtractReportComponent } from './domain/reports/report/parker-extract/parker-extract-report/parker-extract-report.component';
import { DeviceStatusReportComponent } from './domain/reports/report/device-status/device-status-report/device-status-report.component';
import { LinkParkerToUserComponent } from './domain/link-parker-to-user/link-parker-to-user.component';
import { ConfirmEmailComponent } from './base/confirm-email/confirm-email.component';
import { ClientUserGuard } from './base/guards/client-user.guard';
import { TagsAdministrationComponent } from './domain/administration/tags-administration/tags-administration.component';
import { ParkingComponent } from './domain/parking/parking/parking.component';
import { TranslationLoaderResolver } from './base/resolver/translation-loader.resolver';
import { DeviceInteractionComponent } from './domain/device-interaction/device-interaction.component';
import { ParkingAdministrationComponent } from './domain/parking/parking-administration/parking-administration.component';
import { UserGuard } from './base/guards/user.guard';
import { ClientActiveGuard } from './base/guards/client-active.guard';
import { ParkingService } from './domain/parking/parking/parking.service';
import { PaymentReportComponent } from './domain/reports/report/payment/payment-report/payment-report.component';
import { EditProfileService } from './base/edit-profile/edit-profile.service';
import { AdministrationComponent } from './domain/administration/administration/administration.component';
import { ZoneDeviceDetailsService } from './domain/administration/administration/administration-details/zone-device-details/zone-device-details.service';
import { UserExtractReportComponent } from './domain/reports/report/user-extract/user-extract-report/user-extract-report.component';
import { canDeactivateGuardFn } from './base/guards/can-deactivate-guard';
import { AdminOrClientAdminOrCompanyAdminGuard } from './base/guards/admin-client-company-admin.guard';
import { OnboardingWizardComponent } from './domain/administration/onboarding-wizard/onboarding-wizard.component';
import { TagMasterComponent } from './domain/administration/tag-master/tag-master.component';
import { ThirdPartyUserAssignmentAdministrationComponent } from './domain/administration/third-party-user-assignment-administration/third-party-user-assignment-administration.component';
import { ParkingRangerAssignmentAdministrationComponent } from './domain/administration/parking-ranger-assignment-administration/parking-ranger-assignment-administration.component';
import { TwoFactorAuthenticationRegistrationComponent } from './base/two-factor-authentication/two-factor-authentication-registration/two-factor-authentication-registration.component';
import { TwoFactorAuthenticationRecoveryComponent } from './base/two-factor-authentication/two-factor-authentication-recovery/two-factor-authentication-recovery.component';
import { ExportJobComponent } from './domain/reports/export-jobs/export-job.component';
import { FleetListComponent } from './domain/administration/fleet-list/fleet-list.component';
import { RegisterComponent } from './base/register/register.component';
import { ParkingCardAdminComponent } from './domain/parking/parking-card/parking-card-admin/parking-card-admin.component';
import { ParkingCardUserComponent } from './domain/parking/parking-card/parking-card-user/parking-card-user.component';
import { FastCheckinComponent } from './domain/fast-checkin/fast-checkin.component';
import { LayoutType } from './base/models/layout-type';
import { PaymentSuccessfulComponent } from './domain/payment-successful/payment-successful.component';
import { FastCheckinSessionComponent } from './domain/fast-checkin-session/fast-checkin-session.component';
import { PaymentFailedComponent } from './domain/payment-failed/payment-failed.component';
import { PayTillNowSessionComponent } from './domain/pay-till-now-session/pay-till-now-session.component';
import { MobileLandingComponent } from './domain/mobile-landing/mobile-landing.component';
import { PayTillNowComponent } from './domain/pay-till-now/pay-till-now.component';
import { ProblemReportAdministrationComponent } from './domain/administration/problem-report-administration/problem-report-administration.component';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: pages.homePageName,
  },
  {
    path: pages.homePageName,
    canActivate: [(route, state) => inject(AuthGuard).canActivate(route, state), (route, state) => inject(ClientActiveGuard).canActivate(route, state)],
    resolve: {
      data: DashboardService,
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: HomeComponent,
      },
      {
        path: 'edit',
        component: HomeComponent,
      },
    ],
  },
  {
    path: pages.adminPageName,
    canActivate: [(route, state) => inject(AdminOrClientAdminOrCompanyAdminGuard).canActivate(route, state)],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: pages.clientAdministrationPageName
      },
      {
        path: pages.clientAdministrationPageName,
        component: ClientAdministrationComponent,
      },
      {
        path: pages.userManagementPageName,
        component: UserManagementComponent,
        resolve: {
          data: () => inject(ClientService).resolve(),
        }
      },
      {
        path: pages.userManagementPageName + '/client/:id',
        component: UserManagementComponent,
        resolve: {
          data: () => inject(ClientService).resolve(),
        }
      },
      {
        path: pages.configurationAdministrationPageName,
        component: AdministrationComponent,
        resolve: {
          data: () => inject(ClientService).resolve(),
          data2: () => inject(ZoneDeviceDetailsService).initializeServiceData(),
        },
      },
      {
        path: pages.companyAdministrationPageName,
        component: CompanyAdministrationComponent,
        canDeactivate: [canDeactivateGuardFn],
        resolve: {
          data: () => inject(ClientService).resolve(),
        }
      },
      {
        path: pages.parkerAdministrationPageName,
        component: ParkerAdministrationComponent,
        resolve: {
          data: () => inject(ClientService).resolve(),
        }
      },
      {
        path: pages.fleetListAdministrationPageName,
        component: FleetListComponent,
        resolve: {
          data: () => inject(ClientService).resolve(),
        }
      },
      {
        path: pages.tagsAdministrationPageName,
        component: TagsAdministrationComponent,
        resolve: {
          data: () => inject(ClientService).resolve(),
        }
      },
      {
        path: pages.onBoardingWizardPageName,
        component: OnboardingWizardComponent,
        resolve: {
          data: () => inject(ClientService).resolve(),
        }
      },
      {
        path: pages.tagMasterPageName,
        component: TagMasterComponent,
        resolve: {
          data: () => inject(ClientService).resolve(),
        }
      },
      {
        path: pages.thirdPartyUserZoneAssignmentAdministrationPageName,
        component: ThirdPartyUserAssignmentAdministrationComponent,
        canActivate: [(route, state) => inject(AdminGuard).canActivate(route, state)],
      },
      {
        path: pages.parkingRangerZoneAssignmentAdministrationPageName,
        component: ParkingRangerAssignmentAdministrationComponent,
        canActivate: [(route, state) => inject(AdminGuard).canActivate(route, state)],
      },
      {
        path: pages.problemReportAdministrationPageName,
        component: ProblemReportAdministrationComponent,
        canActivate: [(route, state) => inject(AdminGuard).canActivate(route, state)],
      }
    ]
  },
  {
    path: pages.parkPageName,
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: pages.homePageName
      },
      {
        path: pages.parkingPageName,
        component: ParkingComponent,
        canActivate: [(route, state) => inject(UserGuard).canActivate(route, state)],
        resolve: {
          data: () => inject(TranslationLoaderResolver).resolve(),
          data2: () => inject(ParkingService).resolve(),
        }
      },
      {
        path: pages.automaticParkingPageName,
        component: ParkingAdministrationComponent,
        canActivate: [(route, state) => inject(UserGuard).canActivate(route, state)],
        resolve: {
          data: () => inject(TranslationLoaderResolver).resolve(),
        }
      },
      {
        path: pages.parkerRegistrationsPageName,
        component: ParkerRegistrationComponent,
        canActivate: [(route, state) => inject(AdminOrClientAdminOrCompanyAdminGuard).canActivate(route, state)],
        resolve: {
          data: () => inject(ClientService).resolve(),
        }
      },
      {
        path: pages.parkingRangerSessionsPageName,
        component: ParkingRangerSessionsComponent,
        canActivate: [(route, state) => inject(AdminOrClientAdminGuard).canActivate(route, state)],
        canDeactivate: [canDeactivateGuardFn],
        resolve: {
          data: () => inject(ClientService).resolve(),
        }
      },
      {
        path: pages.zoneDamagePageName,
        component: ZoneDamageComponent,
        canActivate: [(route, state) => inject(AdminOrClientAdminGuard).canActivate(route, state)],
        resolve: {
          data: () => inject(ClientService).resolve(),
        }
      },
      {
        path: pages.deviceInteractionPageName,
        component: DeviceInteractionComponent,
        canActivate: [(route, state) => inject(AdminOrClientAdminGuard).canActivate(route, state)],
        resolve: {
          data: () => inject(ClientService).resolve(),
        }
      },
      {
        path: pages.parkingCardPageName,
        component: ParkingCardUserComponent,
        canActivate: [(route, state) => inject(AuthGuard).canActivate(route, state)]
      },
      {
        path: pages.parkingCardAdminPageName,
        component: ParkingCardAdminComponent,
        canActivate: [(route, state) => inject(AdminOrClientAdminOrCompanyAdminGuard).canActivate(route, state)],
      },
    ]
  },
  {
    path: pages.reportsPageName,
    canActivate: [(route, state) => inject(AuthGuard).canActivate(route, state)],
    resolve: {
      data: ClientService
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: pages.savedReportsPageName
      },
      {
        path: pages.exportJobsPageName,
        component: ExportJobComponent,
        canActivate: [(route, state) => inject(AdminOrClientAdminOrCompanyAdminGuard).canActivate(route, state)],
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: ExportJobComponent,
          },
          {
            path: ":id",
            component: ExportJobComponent,
          },
          {
            path: pages.exportJobsCreatePageName,
            component: ExportJobComponent,
          }]
      },
      {
        path: pages.savedReportsPageName,
        component: ReportsComponent,
        canDeactivate: [canDeactivateGuardFn]
      },
      {
        path: pages.tagsPerParkerPageName,
        canActivate: [(route, state) => inject(ClientUserGuard).canActivate(route, state)],
        component: TagsReportComponent,
      },
      {
        path: pages.parkerRegistrationsPageName,
        canActivate: [(route, state) => inject(ClientUserGuard).canActivate(route, state)],
        component: ParkerRegistrationReportComponent,
      },
      {
        path: pages.falseRecordingsPageName,
        canActivate: [(route, state) => inject(ClientUserGuard).canActivate(route, state)],
        component: FalseRecordingReportComponent,
      },
      {
        path: pages.parkerTurnoverPageName,
        canActivate: [(route, state) => inject(ClientUserGuard).canActivate(route, state)],
        component: ParkerTurnoverReportComponent,
      },
      {
        path: pages.parkerExtractPageName,
        canActivate: [(route, state) => inject(AuthGuard).canActivate(route, state)],
        component: ParkerExtractReportComponent,
      },
      {
        path: pages.deviceStatusPageName,
        canActivate: [(route, state) => inject(ClientUserGuard).canActivate(route, state)],
        component: DeviceStatusReportComponent,
      },
      {
        path: pages.automaticParkingPageName,
        canActivate: [(route, state) => inject(AdminGuard).canActivate(route, state)],
        component: AutomaticParkingReportComponent
      },
      {
        path: pages.paymentPageName,
        canActivate: [(route, state) => inject(AuthGuard).canActivate(route, state)],
        component: PaymentReportComponent
      },
      {
        path: pages.userExtractPageName,
        canActivate: [(route, state) => inject(UserGuard).canActivate(route, state)],
        component: UserExtractReportComponent
      }
    ],
  },
  {
    path: pages.loginPageName,
    component: LoginComponent,
  },
  {
    path: pages.editProfilePageName,
    component: EditProfileComponent,
    canActivate: [(route, state) => inject(AuthGuard).canActivate(route, state)],
    resolve: {
      data: () => inject(TranslationLoaderResolver).resolve(),
      data2: () => inject(EditProfileService).resolve()
    }
  },
  {
    path: pages.setPasswordPageName,
    component: SetPasswordComponent,
  },
  {
    path: pages.changePasswordPageName,
    component: ChangePasswordComponent,
    canActivate: [(route, state) => inject(AuthGuard).canActivate(route, state)]
  },
  {
    path: pages.forgotPasswordPageName,
    component: ForgotPasswordComponent,
  },
  {
    path: pages.registerPageName,
    component: RegisterComponent,
  },
  {
    path: pages.confirmEmailPageName,
    component: ConfirmEmailComponent,
  },
  {
    path: pages.linkParkerToUserPageName,
    component: LinkParkerToUserComponent,
    canActivate: [(route, state) => inject(AuthGuard).canActivate(route, state)],
  },
  {
    path: pages.twoFactorAuthenticationPageName,
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: pages.registrationPageName
      },
      {
        path: pages.registrationPageName,
        component: TwoFactorAuthenticationRegistrationComponent,
      },
      {
        path: pages.recoveryPageName,
        component: TwoFactorAuthenticationRecoveryComponent
      }
    ]
  },
  {
    path: pages.fastCheckinPageName,
    data: { layout: LayoutType.Topbar },
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: FastCheckinComponent,
      },
      {
        path: ':ident',
        component: FastCheckinSessionComponent,
      }
    ]
  },
  {
    path: pages.payTillNowPageName,
    data: { layout: LayoutType.Topbar },
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: PayTillNowComponent,
      },
      {
        path: ':ident',
        component: PayTillNowSessionComponent,
      }
    ]
  },
  {
    path: pages.mobileLandingPageName,
    data: { layout: LayoutType.Topbar },
    component: MobileLandingComponent,
  },
  {
    path: pages.paymentSuccessfulPageName,
    data: { layout: LayoutType.Topbar },
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: PaymentSuccessfulComponent,
      },
      {
        path: ':paymentIdent',
        component: PaymentSuccessfulComponent,
      }
    ]
  },
  {
    path: pages.paymentFailedPageName,
    data: { layout: LayoutType.Topbar },
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: PaymentFailedComponent,
      },
      {
        path: ':ident',
        component: PaymentFailedComponent,
      }
    ]
  },
  {
    path: '**',
    component: LoginComponent,
  },
];
